import { useMemo } from 'react';
import { useCheckoutDispatch, useCheckoutState, useModalDispatch } from '@olo-web/client-state';
import { EAuthEntryPoints, EModalTypes } from '@olo-web/types/enums';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { uniqByProp } from '@organisms/Modals/ApplyOffersModal/functions/uniqByProp';
import { useLoyalty } from '@olo-web/domain/customer/hooks/useLoyalty';
import { useCustomerState } from '@olo-web/client-state';

export const useOffersHelpers = () => {
  const modalDispatch = useModalDispatch();
  const { data: loyalty, isLoading: loyaltyIsLoading } = useLoyalty();
  const { data: order, isLoading: orderIsLoading } = useOrder();
  const { data: merchant } = useMerchant();
  const { selectedOffer } = useCheckoutState();
  const checkoutDispatch = useCheckoutDispatch();
  const customerState = useCustomerState();

  const handleOfferSelection = (offer) => {
    checkoutDispatch({
      type: 'SET_SELECTED_OFFER',
      payload: offer,
    });
  };

  const handleSignInClick = () => {
    modalDispatch({
      type: 'OPEN_MODAL',
      payload: {
        modalKey: EModalTypes.LOGIN,
        analyticsAuthEntryPoint: EAuthEntryPoints.APPLY_OFFERS_MODAL,
      },
    });
  };

  const allRewards = useMemo(
    () => (merchant?.spotOnLoyaltyEnabled && loyalty?.rewards) || {},
    [merchant?.spotOnLoyaltyEnabled, loyalty?.rewards]
  );
  const allDeals = useMemo(() => loyalty?.deals || {}, [loyalty?.deals]);

  const availableOffers = useMemo(() => {
    const availablePromocodes = Object.entries(customerState?.promos).reduce(
      (acc: any, [key, value]: [string, any]) => {
        if (value?.isActive) {
          acc.push({
            ...value,
            promoCode: key,
          });
        }

        return acc;
      },
      []
    );

    const availableRewards = Object.values(allRewards).reduce(
      (accRewards: IAvailableReward[], reward: ILoyaltyItem) => {
        if (parseInt(loyalty?.availableSpots || '') >= reward.spotsToRedeem) {
          accRewards.push({
            promotionType: reward.promotionType,
            promotionName: reward.promotionName,
            promotionId: reward.promotionId.toString(),
            discountValue: reward.discountValue,
            spotsToRedeem: reward.spotsToRedeem,
          });
        }

        return accRewards;
      },
      []
    );

    const availableDeals = Object.values(allDeals).reduce(
      (accDeals: IAvailableDeal[], deal: ILoyaltyItem) => {
        if (Number(deal?.minPurchaseAmount) <= Number(order?.preDiscountSubtotal)) {
          accDeals.push({
            promotionType: deal.promotionType,
            promotionName: deal.promotionName,
            promotionId: deal.promotionId.toString(),
            discountValue: deal.discountValue,
            minPurchaseAmount: deal.minPurchaseAmount,
            promotionEndAt: deal.promotionEndAt,
          });
        }

        return accDeals;
      },
      []
    );

    return uniqByProp(
      [...availableRewards, ...availablePromocodes, ...availableDeals],
      'promotionId'
    );
  }, [loyalty, customerState?.promos, order?.preDiscountSubtotal, allRewards, allDeals]);

  const unavailableOffers = useMemo(() => {
    const unavailablePromoCodes =
      Object.values(customerState?.promos)?.filter((promo: any) => !promo?.isActive) || [];
    const unavailableRewards = Object.values(allRewards).filter(
      (item) => parseInt(loyalty?.availableSpots || '') < item.spotsToRedeem
    );

    const unavailableDeals = Object.values(allDeals).filter(
      (deal) => Number(deal?.minPurchaseAmount) > Number(order?.preDiscountSubtotal)
    );

    return [...unavailableRewards, ...unavailablePromoCodes, ...unavailableDeals];
  }, [loyalty, customerState?.promos, order?.preDiscountSubtotal, allRewards, allDeals]);

  const appliedOffer = useMemo(() => {
    return order?.discounts?.[0];
  }, [order?.discounts]);

  return {
    selectedOffer,
    handleSignInClick,
    handleOfferSelection,
    availableOffers,
    unavailableOffers,
    appliedOffer,
    order,
    loyalty,
    loyaltyIsLoading,
    orderIsLoading,
  };
};
